// Use relative URL in production to avoid Mixed Content issues
// Relative URLs work automatically with the current protocol (HTTP/HTTPS)
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api/v1' : 'https://hse.jibuness.com/api/v1');

let authToken = localStorage.getItem('auth_token');

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

export const getAuthToken = () => {
  return authToken || localStorage.getItem('auth_token');
};

/** URL publique Laravel `/storage/{filePath}` (hors préfixe API). */
export function storagePublicUrl(filePath) {
  if (!filePath) return '';
  const configured = process.env.REACT_APP_API_URL || '';
  let base = '';
  if (configured.startsWith('http')) {
    base = configured.replace(/\/api\/v1\/?$/i, '');
  } else if (typeof window !== 'undefined') {
    base = window.location.origin;
  }
  const clean = String(filePath).replace(/^\//, '');
  return `${base}/storage/${clean}`;
}

export const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();
  
  // Ne pas définir Content-Type par défaut si les headers sont déjà définis
  // ou si le body est FormData (le navigateur le définira automatiquement)
  const isFormData = options.body instanceof FormData;
  
  const headers = {
    'Accept': 'application/json',
    ...options.headers,
  };

  // Ajouter Content-Type seulement si ce n'est pas FormData et si ce n'est pas déjà défini
  if (!isFormData && !options.headers?.['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, config);
    
    if (response.status === 401) {
      // Token expiré ou invalide
      setAuthToken(null);
      window.location.href = '/auth-login';
      return;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: response.status === 429 ? 'Too Many Attempts.' : 'Une erreur est survenue' 
      }));
      const error = new Error(errorData.message || (response.status === 429 ? 'Too Many Attempts.' : 'Une erreur est survenue'));
      error.response = { data: errorData, status: response.status };
      error.errors = errorData.errors;
      throw error;
    }

    // Si responseType est blob, retourner le blob
    if (options.responseType === 'blob') {
      return await response.blob();
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

const uploadWithAuth = (url, formData, options = {}) => {
  const token = getAuthToken();
  const { onUploadProgress, method = 'POST' } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API_BASE_URL}${url}`, true);
    xhr.setRequestHeader('Accept', 'application/json');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (typeof onUploadProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        onUploadProgress(percent);
      };
    }

    xhr.onload = () => {
      let payload = {};
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (_) {
        payload = {};
      }

      if (xhr.status === 401) {
        setAuthToken(null);
        window.location.href = '/auth-login';
        return reject(new Error('Session expirée'));
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onUploadProgress === 'function') onUploadProgress(100);
        return resolve(payload);
      }

      const tooLarge =
        xhr.status === 413 ||
        payload?.exception === 'Illuminate\\Http\\Exceptions\\PostTooLargeException';
      const defaultMessage = tooLarge
        ? 'Le volume total des fichiers dépasse la limite serveur. Réduisez la taille des pièces jointes.'
        : 'Une erreur est survenue';
      const error = new Error(payload.message || defaultMessage);
      error.response = { data: payload, status: xhr.status };
      error.errors = payload.errors;
      return reject(error);
    };

    xhr.onerror = () => reject(new Error('Erreur réseau lors de l’envoi du fichier'));
    xhr.send(formData);
  });
};

const isFileLike = (value) => {
  if (!value || typeof value !== 'object') return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob && typeof value.name === 'string') return true;
  return (
    typeof value.name === 'string' &&
    typeof value.size === 'number' &&
    typeof value.type === 'string' &&
    typeof value.arrayBuffer === 'function'
  );
};

export const api = {
  // Auth
  login: async (email, password) => {
    // Ne pas utiliser fetchWithAuth pour le login car on n'a pas encore de token
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // Laravel : 422 = validation OU ValidationException (ex. mauvais mot de passe → errors.email)
      const firstFieldError =
        data.errors && typeof data.errors === 'object'
          ? Object.values(data.errors).flat().find(Boolean)
          : null;
      const message =
        firstFieldError ||
        data.message ||
        (response.status === 422 ? 'Données de connexion invalides.' : 'Identifiants incorrects');
      const err = new Error(message);
      err.response = { data, status: response.status };
      err.errors = data.errors;
      throw err;
    }

    return await response.json();
  },

  logout: () => 
    fetchWithAuth('/logout', { method: 'POST' }),

  me: () => 
    fetchWithAuth('/me'),

  // Observations
  getObservations: (params = {}) => {
    // Filtrer les paramètres vides pour éviter les problèmes
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        cleanParams[key] = params[key];
      }
    });
    const queryString = new URLSearchParams(cleanParams).toString();
    return fetchWithAuth(`/observations${queryString ? `?${queryString}` : ''}`);
  },

  getObservation: (id) => 
    fetchWithAuth(`/observations/${id}`),
  
  openObservationAttachment: (attachmentId) =>
    fetchWithAuth(`/observations/attachments/${attachmentId}/open`, {
      responseType: 'blob',
    }),

  downloadObservationAttachment: (attachmentId) =>
    fetchWithAuth(`/observations/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    }),

  getObservationCopilotStatus: () =>
    fetchWithAuth('/observations/copilot/status'),

  postObservationCopilotSuggest: (data) =>
    fetchWithAuth('/observations/copilot/suggest', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createObservation: (data, options = {}) => {
    // Vérifier s'il y a des fichiers à envoyer
    const attachmentItems = Array.isArray(data.attachments) ? data.attachments : [];
    const validFiles = attachmentItems.filter((item) => isFileLike(item));
    const hasFiles = attachmentItems.length > 0;
    
    if (hasFiles) {
      // Utiliser FormData si on a des fichiers
      const formData = new FormData();
      
      Object.keys(data).forEach(key => {
        if (key === 'attachments') {
          validFiles.forEach(file => formData.append('attachments[]', file));
        } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          // Convertir les valeurs en chaînes pour FormData
          formData.append(key, data[key] === true ? '1' : data[key] === false ? '0' : String(data[key]));
        }
      });

      return uploadWithAuth('/observations', formData, options);
    } else {
      if (typeof options.onUploadProgress === 'function') {
        options.onUploadProgress(100);
      }
      // Utiliser JSON si pas de fichiers (plus simple et plus fiable)
      return fetchWithAuth('/observations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
    }
  },

  updateObservation: (id, data, options = {}) => {
    const attachmentItems = Array.isArray(data.attachments) ? data.attachments : [];
    const validFiles = attachmentItems.filter((item) => isFileLike(item));
    const hasFiles = attachmentItems.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      formData.append('_method', 'PUT');

      Object.keys(data).forEach((key) => {
        if (key === 'attachments') {
          validFiles.forEach((file) => formData.append('attachments[]', file));
        } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(
            key,
            data[key] === true ? '1' : data[key] === false ? '0' : String(data[key])
          );
        }
      });

      return uploadWithAuth(`/observations/${id}`, formData, options);
    }

    if (typeof options.onUploadProgress === 'function') {
      options.onUploadProgress(100);
    }

    const payloadWithoutAttachments = { ...data };
    if ('attachments' in payloadWithoutAttachments) {
      delete payloadWithoutAttachments.attachments;
    }

    return fetchWithAuth(`/observations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payloadWithoutAttachments),
    });
  },

  deleteObservation: (id) => 
    fetchWithAuth(`/observations/${id}`, {
      method: 'DELETE',
    }),

  // Workflow
  updateObservationStatus: (id, status, data = {}) => 
    fetchWithAuth(`/observations/${id}/workflow`, {
      method: 'POST',
      body: JSON.stringify({ status, ...data }),
    }),

  getWorkflowHistory: (id) => 
    fetchWithAuth(`/observations/${id}/workflow/history`),

  getAllWorkflowHistory: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/workflow/history${queryString ? `?${queryString}` : ''}`);
  },

  getWorkflowDelayPredictions: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/workflow/delay-predictions${queryString ? `?${queryString}` : ''}`);
  },

  // Dashboards
  getHseDashboard: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/dashboard/hse${queryString ? `?${queryString}` : ''}`);
  },

  getManagementDashboard: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/dashboard/management${queryString ? `?${queryString}` : ''}`);
  },

  getOperationalDashboard: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/dashboard/operational-overview${queryString ? `?${queryString}` : ''}`);
  },

  // Sites
  getSites: () => fetchWithAuth('/sites'),
  createSite: (data) => 
    fetchWithAuth('/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSite: (id, data) => 
    fetchWithAuth(`/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteSite: (id) => 
    fetchWithAuth(`/sites/${id}`, {
      method: 'DELETE',
    }),

  // Card of Month
  getCardOfMonth: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/card-of-month${queryString ? `?${queryString}` : ''}`);
  },

  selectCardOfMonth: (observationId) =>
    fetchWithAuth(`/card-of-month/${observationId}/select`, {
      method: 'POST',
    }),

  // Configuration scoring
  getScoringConfig: () => fetchWithAuth('/scoring-config'),
  getScoringConfigDefaults: () => fetchWithAuth('/scoring-config/defaults'),
  updateScoringConfig: (config) =>
    fetchWithAuth('/scoring-config', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }),
  resetScoringConfig: () =>
    fetchWithAuth('/scoring-config/reset', { method: 'POST' }),

  getEmployeeOfMonth: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/card-of-month/employee${queryString ? `?${queryString}` : ''}`);
  },

  // Users
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/users${queryString ? `?${queryString}` : ''}`);
  },

  getUser: (id) => 
    fetchWithAuth(`/users/${id}`),

  createUser: (data) =>
    fetchWithAuth('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUser: (id, data) =>
    fetchWithAuth(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUser: (id) =>
    fetchWithAuth(`/users/${id}`, {
      method: 'DELETE',
    }),

  generateUserPassword: (id) =>
    fetchWithAuth(`/users/${id}/generate-password`, {
      method: 'POST',
    }),

  // Roles
  getRoles: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/roles${queryString ? `?${queryString}` : ''}`);
  },
  getRole: (id) => fetchWithAuth(`/roles/${id}`),
  createRole: (data) =>
    fetchWithAuth('/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateRole: (id, data) =>
    fetchWithAuth(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteRole: (id) =>
    fetchWithAuth(`/roles/${id}`, {
      method: 'DELETE',
    }),

  // Permissions
  getPermissions: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/permissions${queryString ? `?${queryString}` : ''}`);
  },
  getPermissionGroups: () => fetchWithAuth('/permissions/groups'),
  getPermission: (id) => fetchWithAuth(`/permissions/${id}`),
  createPermission: (data) =>
    fetchWithAuth('/permissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updatePermission: (id, data) =>
    fetchWithAuth(`/permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deletePermission: (id) =>
    fetchWithAuth(`/permissions/${id}`, {
      method: 'DELETE',
    }),

  // User Sites
  getUserSites: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/user-sites${queryString ? `?${queryString}` : ''}`);
  },
  getUserSitesStatistics: () => fetchWithAuth('/user-sites/statistics'),
  assignUserSites: (userId, siteIds) =>
    fetchWithAuth(`/users/${userId}/sites`, {
      method: 'POST',
      body: JSON.stringify({ site_ids: siteIds }),
    }),
  removeUserSite: (userId, siteId) =>
    fetchWithAuth(`/users/${userId}/sites/${siteId}`, {
      method: 'DELETE',
    }),
  getSiteUsers: (siteId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/sites/${siteId}/users${queryString ? `?${queryString}` : ''}`);
  },

  // Activity Logs
  getActivityLogs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/activity-logs${queryString ? `?${queryString}` : ''}`);
  },
  getActivityLog: (id) => fetchWithAuth(`/activity-logs/${id}`),
  getActivityLogStatistics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/activity-logs/statistics${queryString ? `?${queryString}` : ''}`);
  },
  getActivityLogsBySubject: (subjectType, subjectId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/activity-logs/${subjectType}/${subjectId}${queryString ? `?${queryString}` : ''}`);
  },

  // Notifications
  getNotifications: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/notifications${queryString ? `?${queryString}` : ''}`);
  },
  getAlerts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/alerts${queryString ? `?${queryString}` : ''}`);
  },
  getPrioritizedAlerts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/alerts/prioritized${queryString ? `?${queryString}` : ''}`);
  },
  getAlertStats: () => fetchWithAuth('/alerts/stats'),
  takeAlertOwnership: (id) =>
    fetchWithAuth(`/alerts/${id}/take-ownership`, {
      method: 'POST',
    }),
  getUnreadNotificationsCount: () => fetchWithAuth('/notifications/unread-count'),
  markNotificationAsRead: (id) =>
    fetchWithAuth(`/notifications/${id}/read`, {
      method: 'POST',
    }),
  markAllNotificationsAsRead: () =>
    fetchWithAuth('/notifications/mark-all-read', {
      method: 'POST',
    }),
  deleteNotification: (id) =>
    fetchWithAuth(`/notifications/${id}`, {
      method: 'DELETE',
    }),

  // Analytics
  getAnalytics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/analytics${queryString ? `?${queryString}` : ''}`);
  },

  getRiskScore7d: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/analytics/risk-score-7d${queryString ? `?${queryString}` : ''}`);
  },

  // Reports
  getMonthlyReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/reports/monthly${queryString ? `?${queryString}` : ''}`);
  },
  downloadMonthlyReportPDF: async (params = {}) => {
    const queryString = new URLSearchParams({ ...params, format: 'pdf' }).toString();
    const blob = await fetchWithAuth(`/reports/monthly?${queryString}`, {
      responseType: 'blob',
    });
    return blob;
  },
  getSiteReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/reports/site${queryString ? `?${queryString}` : ''}`);
  },
  downloadSiteReportPDF: async (params = {}) => {
    const queryString = new URLSearchParams({ ...params, format: 'pdf' }).toString();
    const blob = await fetchWithAuth(`/reports/site?${queryString}`, {
      responseType: 'blob',
    });
    return blob;
  },
  getCustomReport: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        params[key].forEach((value) => {
          searchParams.append(`${key}[]`, value);
        });
      } else {
        searchParams.append(key, params[key]);
      }
    });
    const queryString = searchParams.toString();
    return fetchWithAuth(`/reports/custom${queryString ? `?${queryString}` : ''}`);
  },
  postIntelligentReport: (data) =>
    fetchWithAuth('/reports/intelligent', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  downloadCustomReportPDF: async (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        params[key].forEach((value) => {
          searchParams.append(`${key}[]`, value);
        });
      } else {
        searchParams.append(key, params[key]);
      }
    });
    searchParams.append('format', 'pdf');
    const queryString = searchParams.toString();
    const blob = await fetchWithAuth(`/reports/custom?${queryString}`, {
      responseType: 'blob',
    });
    return blob;
  },

  // Risk Zones (Cartographie des risques)
  getRiskZones: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/risk-zones${queryString ? `?${queryString}` : ''}`);
  },

  getRiskZone: (id) => 
    fetchWithAuth(`/risk-zones/${id}`),

  createRiskZone: (data) =>
    fetchWithAuth('/risk-zones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRiskZone: (id, data) =>
    fetchWithAuth(`/risk-zones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRiskZone: (id) =>
    fetchWithAuth(`/risk-zones/${id}`, {
      method: 'DELETE',
    }),

  getRiskZonesForMap: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/risk-zones/map/data${queryString ? `?${queryString}` : ''}`);
  },

  getObservationsForMap: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/risk-zones/map/observations${queryString ? `?${queryString}` : ''}`);
  },

  getRiskZonesStatistics: () => 
    fetchWithAuth('/risk-zones/statistics'),

  getRiskZoneObservations: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/risk-zones/${id}/observations${queryString ? `?${queryString}` : ''}`);
  },

  markRiskZoneReviewed: (id) =>
    fetchWithAuth(`/risk-zones/${id}/mark-reviewed`, {
      method: 'POST',
    }),

  // Risk Zone Actions
  getRiskZoneActions: (riskZoneId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/risk-zones/${riskZoneId}/actions${queryString ? `?${queryString}` : ''}`);
  },

  createRiskZoneAction: (riskZoneId, data) =>
    fetchWithAuth(`/risk-zones/${riskZoneId}/actions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRiskZoneAction: (riskZoneId, actionId, data) =>
    fetchWithAuth(`/risk-zones/${riskZoneId}/actions/${actionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRiskZoneAction: (riskZoneId, actionId) =>
    fetchWithAuth(`/risk-zones/${riskZoneId}/actions/${actionId}`, {
      method: 'DELETE',
    }),

  // Gamification
  getSiteSecurityScores: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/gamification/site-scores${queryString ? `?${queryString}` : ''}`);
  },

  getLeaderboard: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/gamification/leaderboard${queryString ? `?${queryString}` : ''}`);
  },

  getMyStats: () => 
    fetchWithAuth('/gamification/my-stats'),

  getMyPointsHistory: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/gamification/my-points-history${queryString ? `?${queryString}` : ''}`);
  },

  getBadges: () => 
    fetchWithAuth('/gamification/badges'),

  getMyBadges: () => 
    fetchWithAuth('/gamification/my-badges'),

  getSiteLeaderboard: (siteId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/gamification/sites/${siteId}/leaderboard${queryString ? `?${queryString}` : ''}`);
  },

  getUserStats: (userId) => 
    fetchWithAuth(`/gamification/users/${userId}/stats`),

  // Compliance
  getAuditTrail: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/audit-trail${queryString ? `?${queryString}` : ''}`);
  },

  getTraceability: (subjectType, subjectId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/traceability/${subjectType}/${subjectId}${queryString ? `?${queryString}` : ''}`);
  },

  generateAuditTrailReport: (data) => 
    fetchWithAuth('/compliance/audit-trail/report', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAuditReports: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/audit-reports${queryString ? `?${queryString}` : ''}`);
  },

  getAuditReport: (id) => 
    fetchWithAuth(`/compliance/audit-reports/${id}`),

  getIsoCompliance: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/iso${queryString ? `?${queryString}` : ''}`);
  },

  getIsoComplianceStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/iso/stats${queryString ? `?${queryString}` : ''}`);
  },

  getComplianceIntelligentAssistant: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return fetchWithAuth(`/compliance/intelligent-assistant${queryString ? `?${queryString}` : ''}`);
  },

  createIsoCompliance: (data) => 
    fetchWithAuth('/compliance/iso', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateIsoCompliance: (id, data) => 
    fetchWithAuth(`/compliance/iso/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteIsoCompliance: (id) => 
    fetchWithAuth(`/compliance/iso/${id}`, {
      method: 'DELETE',
    }),

  // IA prédictive (Groq — backend uniquement)
  getPredictiveAiStatus: () => fetchWithAuth('/predictive-ai/status'),

  postPredictiveAiInsights: (data = {}) =>
    fetchWithAuth('/predictive-ai/insights', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // —— Processus HSE : inspections (modèles + inspections terrain)
  getInspectionTemplates: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/inspection-templates${queryString ? `?${queryString}` : ''}`);
  },
  getInspectionTemplate: (id) => fetchWithAuth(`/inspection-templates/${id}`),
  createInspectionTemplate: (data) =>
    fetchWithAuth('/inspection-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateInspectionTemplate: (id, data) =>
    fetchWithAuth(`/inspection-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInspectionTemplate: (id) =>
    fetchWithAuth(`/inspection-templates/${id}`, { method: 'DELETE' }),

  getInspections: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/inspections${queryString ? `?${queryString}` : ''}`);
  },
  getInspection: (id) => fetchWithAuth(`/inspections/${id}`),
  createInspection: (data) =>
    fetchWithAuth('/inspections', { method: 'POST', body: JSON.stringify(data) }),
  updateInspection: (id, data) =>
    fetchWithAuth(`/inspections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  submitInspection: (id) =>
    fetchWithAuth(`/inspections/${id}/submit`, { method: 'POST' }),
  getInspectionReport: (id) => fetchWithAuth(`/inspections/${id}/report`),
  uploadInspectionAnswerPhoto: (inspectionId, answerId, file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return uploadWithAuth(`/inspections/${inspectionId}/answers/${answerId}/photo`, fd);
  },

  getInspectionRecurrenceRules: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/inspection-recurrence-rules${queryString ? `?${queryString}` : ''}`);
  },
  getInspectionRecurrenceRule: (id) => fetchWithAuth(`/inspection-recurrence-rules/${id}`),
  createInspectionRecurrenceRule: (data) =>
    fetchWithAuth('/inspection-recurrence-rules', { method: 'POST', body: JSON.stringify(data) }),
  updateInspectionRecurrenceRule: (id, data) =>
    fetchWithAuth(`/inspection-recurrence-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInspectionRecurrenceRule: (id) =>
    fetchWithAuth(`/inspection-recurrence-rules/${id}`, { method: 'DELETE' }),

  // Permis de travail (PTW)
  getPermits: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/permits${queryString ? `?${queryString}` : ''}`);
  },
  getPermit: (id) => fetchWithAuth(`/permits/${id}`),
  createPermit: (data) =>
    fetchWithAuth('/permits', { method: 'POST', body: JSON.stringify(data) }),
  updatePermit: (id, data) =>
    fetchWithAuth(`/permits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  submitPermit: (id) =>
    fetchWithAuth(`/permits/${id}/submit`, { method: 'POST' }),
  approvePermitStep: (permitId, stepId, data = {}) =>
    fetchWithAuth(`/permits/${permitId}/steps/${stepId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  rejectPermitStep: (permitId, stepId, data) =>
    fetchWithAuth(`/permits/${permitId}/steps/${stepId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  activatePermit: (id) =>
    fetchWithAuth(`/permits/${id}/activate`, { method: 'POST' }),
  closePermit: (id, data = {}) =>
    fetchWithAuth(`/permits/${id}/close`, { method: 'POST', body: JSON.stringify(data) }),

  // MOC
  getMocRequests: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/moc-requests${queryString ? `?${queryString}` : ''}`);
  },
  getMocRequest: (id) => fetchWithAuth(`/moc-requests/${id}`),
  createMocRequest: (data) =>
    fetchWithAuth('/moc-requests', { method: 'POST', body: JSON.stringify(data) }),
  updateMocRequest: (id, data) =>
    fetchWithAuth(`/moc-requests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  postMocRiskAssessment: (mocId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/risk-assessments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  submitMocForReview: (mocId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/submit-review`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  approveMoc: (mocId, approvalId, data = {}) =>
    fetchWithAuth(`/moc-requests/${mocId}/approvals/${approvalId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  rejectMoc: (mocId, approvalId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/approvals/${approvalId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  startMocImplementation: (mocId) =>
    fetchWithAuth(`/moc-requests/${mocId}/implementation/start`, { method: 'POST' }),
  completeMocImplementation: (mocId) =>
    fetchWithAuth(`/moc-requests/${mocId}/implementation/complete`, { method: 'POST' }),
  submitMocPostReview: (mocId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/post-review`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createMocAction: (mocId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/actions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMocAction: (mocId, actionId, data) =>
    fetchWithAuth(`/moc-requests/${mocId}/actions/${actionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Incidents & RCA
  getIncidents: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/incidents${queryString ? `?${queryString}` : ''}`);
  },
  getIncident: (id) => fetchWithAuth(`/incidents/${id}`),
  createIncident: (data) =>
    fetchWithAuth('/incidents', { method: 'POST', body: JSON.stringify(data) }),
  startIncidentInvestigation: (id, data = {}) =>
    fetchWithAuth(`/incidents/${id}/investigation/start`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  addIncidentRootCause: (id, data) =>
    fetchWithAuth(`/incidents/${id}/causes/root`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  addIncidentFiveWhy: (id, data) =>
    fetchWithAuth(`/incidents/${id}/causes/five-whys`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  upsertIncidentFishbone: (id, data) =>
    fetchWithAuth(`/incidents/${id}/fishbone`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createIncidentCapa: (id, data) =>
    fetchWithAuth(`/incidents/${id}/capa`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateIncidentCapa: (id, actionId, data) =>
    fetchWithAuth(`/incidents/${id}/capa/${actionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  uploadIncidentAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return uploadWithAuth(`/incidents/${id}/attachments`, fd);
  },
  getIncidentRcaReport: (id) => fetchWithAuth(`/incidents/${id}/rca-report`),
  postIncidentRcaAiSuggest: (id) =>
    fetchWithAuth(`/incidents/${id}/rca/ai-suggest`, { method: 'POST' }),
  closeIncident: (id) =>
    fetchWithAuth(`/incidents/${id}/close`, { method: 'POST' }),

  // Actifs HSE
  getAssetTypes: () => fetchWithAuth('/asset-types'),
  createAssetType: (data) =>
    fetchWithAuth('/asset-types', { method: 'POST', body: JSON.stringify(data) }),
  updateAssetType: (id, data) =>
    fetchWithAuth(`/asset-types/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAssetType: (id) =>
    fetchWithAuth(`/asset-types/${id}`, { method: 'DELETE' }),

  getAssets: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    return fetchWithAuth(`/assets${queryString ? `?${queryString}` : ''}`);
  },
  getAsset: (id) => fetchWithAuth(`/assets/${id}`),
  createAsset: (data) =>
    fetchWithAuth('/assets', { method: 'POST', body: JSON.stringify(data) }),
  updateAsset: (id, data) =>
    fetchWithAuth(`/assets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAsset: (id) =>
    fetchWithAuth(`/assets/${id}`, { method: 'DELETE' }),
  linkAssetRiskZone: (assetId, riskZoneId) =>
    fetchWithAuth(`/assets/${assetId}/risk-zones/${riskZoneId}`, { method: 'POST' }),
  unlinkAssetRiskZone: (assetId, riskZoneId) =>
    fetchWithAuth(`/assets/${assetId}/risk-zones/${riskZoneId}`, { method: 'DELETE' }),
  linkAssetIncident: (assetId, incidentId) =>
    fetchWithAuth(`/assets/${assetId}/incidents/${incidentId}`, { method: 'POST' }),
  linkAssetInspection: (assetId, inspectionId) =>
    fetchWithAuth(`/assets/${assetId}/inspections/${inspectionId}`, { method: 'POST' }),
  getAssetSafetyHistory: (id) => fetchWithAuth(`/assets/${id}/safety-history`),
  postAssetRiskScore: (id) =>
    fetchWithAuth(`/assets/${id}/risk-score`, { method: 'POST' }),

};

